import { useMaybeAssignCategoryTreeExperiment } from "@faire/deprecated-retailer-visitor-shared/lib/useMaybeAssignCategoryTreeExperiment";
import { Category } from "@faire/deprecated-retailer-visitor-shared/models/Category/Category";
import { getRetailer } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getRetailer";
import { useSuspenseLoggedInNavigationQuery } from "@faire/deprecated-retailer-visitor-shared/stores/domain/useNavigationQuery";
import { Core } from "@faire/design-tokens";
import { trackCategoriesSideNavigationClick } from "@faire/web-api--source/events/categories/click/sideNavigation";
import { IRecordCategoryNodesVisibilityRequest } from "@faire/web-api--source/indigofair/category/IRecordCategoryNodesVisibilityRequest";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import { INavigationLink } from "@faire/web-api--source/indigofair/data/INavigationLink";
import { INavigationLinkGroup } from "@faire/web-api--source/indigofair/data/INavigationLinkGroup";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Weight } from "@faire/web/slate/Font/utils";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Span, Typography } from "@faire/web/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { Link } from "@faire/web/ui/LinkAnchor";
import flatMap from "lodash/flatMap";
import uniq from "lodash/uniq";
import * as React from "react";
import { useMemo } from "react";
import styled, { css } from "styled-components";

const FEATURED_NAV_GROUP_UNIQUE_ID = "/nav_link/featured";

interface FeaturedLeafLink {
  name: string;
  to: string;
  pathIdentifier?: string;
  isVisible: boolean;
  shouldRecordPathIdentifier: boolean;
}

interface FeaturedNavLinkSection {
  sectionHeader: string;
  links: FeaturedLeafLink[];
  pathIdentifier?: string;
}

/**
 * Create structure for each leaf link
 */
const convertSublinkToLeafLink = (
  navLink: INavigationLink
): FeaturedLeafLink => {
  return {
    name: navLink.name ?? "",
    to: navLink.deep_link_path ?? "",
    pathIdentifier: navLink.path_identifier,
    isVisible:
      navLink.style_group !== "INVISIBLE" &&
      navLink.style_group !== "INVISIBLE_FOR_ASSIGNMENT",
    shouldRecordPathIdentifier: navLink.style_group !== "INVISIBLE",
  };
};

/**
 * Creates structure for each group header navlink that contains leaf links
 */
const convertSublinkToLinkSection = (
  navLink: INavigationLink
): FeaturedNavLinkSection => {
  const { name } = navLink;

  return {
    sectionHeader: name ?? "",
    links: navLink.sublinks.map(convertSublinkToLeafLink),
    pathIdentifier: navLink.path_identifier,
  };
};

/**
 * Create list of navlink sections
 */
const getFeaturedNavLinkSections = (navLinkGroups: INavigationLinkGroup[]) => {
  // the Featured navlink will be the first one in the Featured group's navlinks list
  const featuredNavLink = navLinkGroups.find(
    (group) => group.unique_identifier === FEATURED_NAV_GROUP_UNIQUE_ID
  )?.navigation_links[0];

  if (!featuredNavLink) {
    return null;
  }

  return featuredNavLink.sublinks.map(convertSublinkToLinkSection);
};

export const PrunedFeaturedSideNavigation = ({
  selectedCategory,
}: {
  selectedCategory?: Category | undefined;
}) => {
  const { navigationLinkGroup } = useSuspenseLoggedInNavigationQuery(
    ClientPlatformType.CLIENT_DESKTOP,
    getRetailer()?.token ?? ""
  );
  const linkSections = getFeaturedNavLinkSections(navigationLinkGroup);
  const maybeAssignCategoryTreeExperiment =
    useMaybeAssignCategoryTreeExperiment();

  const pathIdentifiersToRecord = useMemo(
    () =>
      uniq(
        flatMap(linkSections, (section) => [
          section.pathIdentifier,
          ...section.links.map((link) =>
            link.shouldRecordPathIdentifier ? link.pathIdentifier : undefined
          ),
        ])
      ).filter(isNotUndefined),
    [linkSections]
  );

  const onVisible = useDoActionWhenVisible(() => {
    maybeAssignCategoryTreeExperiment(
      () => pathIdentifiersToRecord,
      IRecordCategoryNodesVisibilityRequest.Source.WEB_DESKTOP_LEFT_NAV
    );
  });

  if (!linkSections || linkSections.length === 0) {
    return null;
  }

  return (
    <Container data-test-id="category-page-sidebar" ref={onVisible}>
      {linkSections.map((section) => (
        <CollectionsCategorySection
          linkSection={section}
          key={section.sectionHeader}
          selectedCategory={selectedCategory}
        />
      ))}
    </Container>
  );
};

const Container = styled.nav`
  margin-bottom: ${getSpacing("1x")};
`;

const CollectionsCategorySection: React.FC<{
  linkSection: FeaturedNavLinkSection;
  selectedCategory?: Category;
}> = ({ linkSection, selectedCategory }) => {
  const { sectionHeader, links } = linkSection;
  const listHeaderId = `category-list-header-${sectionHeader
    .toLowerCase()
    .replace(" ", "-")}`;

  return (
    <>
      {sectionHeader ? (
        <>
          <Typography variant="paragraphSansMedium" id={listHeaderId}>
            {sectionHeader}
          </Typography>
          <Spacer height="1x" />
        </>
      ) : null}
      <ul aria-labelledby={listHeaderId}>
        {links
          .filter((link) => link.isVisible)
          .map(({ name, pathIdentifier, to }, i) => {
            const isSelected =
              selectedCategory?.urlId.id?.toLowerCase() === name.toLowerCase();
            return (
              <li key={to + i}>
                <CategoryLink
                  onClick={() =>
                    trackCategoriesSideNavigationClick(pathIdentifier ?? "")
                  }
                  to={to}
                >
                  <CategoryText selected={isSelected}>{name}</CategoryText>
                </CategoryLink>
              </li>
            );
          })}
      </ul>
      <Spacer height="3x" />
    </>
  );
};

const CategoryText = styled(Span)<{
  selected?: boolean;
}>`
  &&& {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    padding-bottom: 2px;
    font-weight: ${(props) => (props.selected ? Weight.medium : Weight.book)};
    border-bottom: ${(props) =>
      props.selected ? css`1px solid ${Core.border.default}` : 0};
    &:hover {
      border-bottom: 1px solid ${Core.border.default};
    }
  }
`;

const CategoryLink = styled(Link)`
  display: block;
  width: 100%;
  cursor: pointer !important;
  font-size: 12px;
  line-height: 32px;
  opacity: 0.82;
  padding: 0;
`;
